import { useMemo } from 'react';

import type { CMSApiBase, CMSImageBase } from '@/api/cms/base';
import type { CMSTournamentGame } from '@/api/cms/restrictions';
import {
  isPromoAvailableDate,
  isPromoAvailableOrComingSoon,
} from '@/helpers/dateTime';
import type { IContentByLicense } from '@/modules/license/helpers';
import { useGetCommonStore } from '@/store';

export interface IMobileAppCMS {
  showAndroid: boolean;
  androidApk?: CMSImageBase;
}

interface IBrandAssets {
  logoDesktop: CMSImageBase | null;
  logoMobile: CMSImageBase | null;
  preloader: CMSImageBase | null;
  vipAnimatedLogo: CMSImageBase | null;
  animatedLogo: boolean;
}

interface ICMSConfigDateRange {
  start: string;
  end: string;
}

interface IHeadlinePromoCMSEntry extends ICMSConfigDateRange {
  promoKey: string;
}

export interface ICMSConfigState {
  bonanzaPromo: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  scratchGame: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  fortuneWheel: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  diceRoll: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  quest: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  mission: {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
  headlinePromo: (promoKey: string) => {
    dateRange?: ICMSConfigDateRange;
    availableByDate: boolean;
  };
}

export interface ICMSConfigApi extends CMSApiBase {
  bonanzaPromo?: ICMSConfigDateRange;
  scratchGame?: ICMSConfigDateRange;
  fortuneWheel?: ICMSConfigDateRange;
  diceRoll?: ICMSConfigDateRange;
  quest?: ICMSConfigDateRange;
  mission?: ICMSConfigDateRange;
  headlinePromo?: IHeadlinePromoCMSEntry[];
  mobileApp?: IMobileAppCMS;
  brandAssets?: IBrandAssets;
  tournamentGames?: CMSTournamentGame[];
  copyrightByLicense?: IContentByLicense[];
}

export const useCMSConfig = () => {
  const useCommonStore = useGetCommonStore();

  const data = useCommonStore.use.cmsConfig();

  const cmsConfig: ICMSConfigState | null = useMemo(() => {
    if (!data) return null;
    return {
      bonanzaPromo: {
        dateRange: data.bonanzaPromo,
        availableByDate: isPromoAvailableDate(
          data.bonanzaPromo?.start,
          data.bonanzaPromo?.end
        ),
      },
      scratchGame: {
        dateRange: data.scratchGame,
        availableByDate: isPromoAvailableDate(
          data.scratchGame?.start,
          data.scratchGame?.end
        ),
      },
      fortuneWheel: {
        dateRange: data.fortuneWheel,
        availableByDate: isPromoAvailableOrComingSoon(
          data.fortuneWheel?.start,
          data.fortuneWheel?.end
        ),
      },
      diceRoll: {
        dateRange: data.diceRoll,
        availableByDate: isPromoAvailableDate(
          data.diceRoll?.start,
          data.diceRoll?.end
        ),
      },
      quest: {
        dateRange: data.quest,
        availableByDate: isPromoAvailableDate(
          data.quest?.start,
          data.quest?.end
        ),
      },
      mission: {
        dateRange: data.mission,
        availableByDate: isPromoAvailableDate(
          data.mission?.start,
          data.mission?.end
        ),
      },
      headlinePromo: (promoKey: string) => {
        const promos = Array.isArray(data.headlinePromo)
          ? data.headlinePromo
          : [];
        const entry = promos.find((item) => item.promoKey === promoKey);
        return {
          dateRange: entry,
          availableByDate: isPromoAvailableDate(entry?.start, entry?.end),
        };
      },
    };
  }, [data]);

  const mobileAppCMS = useMemo(() => {
    if (!data) return null;
    return data.mobileApp || null;
  }, [data]);

  const brandAssets = useMemo(() => {
    if (!data) return null;
    return data.brandAssets || null;
  }, [data]);

  const tournamentGames = useMemo(() => {
    if (!data) return [];
    return data.tournamentGames || [];
  }, [data]);

  return { data, cmsConfig, mobileAppCMS, brandAssets, tournamentGames };
};
